import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LayoutService } from '@app/core/services/layout.service';
import { FirstTimeLocalStorageService } from '@app/core/services/first-time-local-storage/first-time-local-storage.service';
import { Popover } from 'primeng/popover';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Tooltip } from 'primeng/tooltip';
import { SupportMenuMixpanelEventTrackingService } from './support-menu-mixpanel-event-tracking.service';

@Component({
  selector: 'app-support-menu',
  templateUrl: './support-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, Button, AngularSvgIconModule, Tooltip, Popover],
})
export class SupportMenuComponent implements AfterViewInit {
  // constants
  readonly FEATURE_KEY = 'support-menu' as const;
  readonly TOOLTIP_DISMISSED_KEY = 'tooltip-dismissed' as const;
  readonly MENU_LAYOUT = 'menu' as const;
  readonly STANDALONE_LAYOUT = 'standalone' as const;
  private readonly SUPPORT_TICKET_URL =
    'https://support.agrigateone.com/tickets-view';
  private readonly SUPPORT_DOCS_URL =
    'https://support.agrigateone.com/en/knowledge-base';

  // dependencies
  layoutService = inject(LayoutService);
  private _elRef = inject(ElementRef);
  private _firstTimeLocalStorageService = inject(FirstTimeLocalStorageService);
  private _supportMenuMixpanelTracking = inject(
    SupportMenuMixpanelEventTrackingService,
  );

  // view references
  @ViewChild('supportMenuPopover')
  private _supportMenuPopover!: Popover;

  @ViewChild('firstTimePopover')
  firstTimePopover!: Popover;

  // input
  readonly layout = input<'menu' | 'standalone'>(this.STANDALONE_LAYOUT);

  // computed signals
  readonly isMenuLayout = computed(() => this.layout() === this.MENU_LAYOUT);
  readonly isStandaloneLayout = computed(
    () => this.layout() === this.STANDALONE_LAYOUT,
  );

  // first-time tooltip state
  readonly firstTimePopoverDismissed = signal<boolean>(false);
  readonly showFirstTimePopover = computed<boolean>(
    () => this.isStandaloneLayout() && !this.firstTimePopoverDismissed(),
  );

  constructor() {
    this._initFirstTimePopover();

    // hide the popover reactively when dismissed
    effect(() => {
      const showFirstTimePopover: boolean = this.showFirstTimePopover();
      if (!showFirstTimePopover) {
        this.firstTimePopover?.hide();
      }
    });
  }

  ngAfterViewInit(): void {
    // Ensure that the popover has rendered before we show it
    if (this.showFirstTimePopover()) {
      this._openFirstTimePopover();
    }
  }

  private _initFirstTimePopover(): void {
    const alreadyDismissed: boolean =
      !this._firstTimeLocalStorageService.isFirstTime(
        this.FEATURE_KEY,
        this.TOOLTIP_DISMISSED_KEY,
      );

    this.firstTimePopoverDismissed.set(alreadyDismissed);
  }

  private _openFirstTimePopover(): void {
    if (this.firstTimePopover?.overlayVisible) {
      return;
    }

    this._supportMenuMixpanelTracking.trackTooltipShown();
    this.firstTimePopover?.show(null, this._elRef.nativeElement);
  }

  dismissFirstTimePopover(): void {
    this.firstTimePopoverDismissed.set(true);
    this.firstTimePopover.hide();
    this._firstTimeLocalStorageService.setLocalStorage(
      this.FEATURE_KEY,
      this.TOOLTIP_DISMISSED_KEY,
    );
    this._supportMenuMixpanelTracking.trackTooltipDismissed();
  }

  onMenuButtonClick(event: Event): void {
    if (!this._supportMenuPopover.overlayVisible) {
      this._supportMenuMixpanelTracking.trackMenuOpened();
    }

    this._supportMenuPopover.toggle(event);
  }

  openSupportTicket(): void {
    this._supportMenuMixpanelTracking.trackActionClicked('log_ticket');
    this._openLinkInNewTab(this.SUPPORT_TICKET_URL);
  }

  openSupportDocs(): void {
    this._supportMenuMixpanelTracking.trackActionClicked('view_docs');
    this._openLinkInNewTab(this.SUPPORT_DOCS_URL);
  }

  private _openLinkInNewTab(url: string): void {
    if (!url) {
      return;
    }

    // prevent the new tab from accessing the parent window's properties
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
